// Budgets in BattleOps (CFO plan 5.2). The books keep them (booksets/{bs}/budgets, saveBudget / deleteBudget) and
// publish each one's current period to opsCfo/now.budgets — with the money actual from the ledger. Hours and metric
// budgets come back with actual = null: they're counted here, from the clock and the metric values. Pure.
//
// Periods mirror the books (functions/src/lib/budgets.ts): months are calendar months; quarters and years start on
// the fiscal start (DBR Feb 1). Rollover carries what was left (or overspent) earlier in the same fiscal year.

import { localDateStr } from './time.js';
import { canonTag, resolveTags, tagIndex as tagIndexOf } from './tags.js';
import { jobTypeOf } from './profit.js';
import { valuesFor, rollup, aggregate } from './metrics.js';

const H = 3600000;
const DAY = 86400000;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round(v * 100) / 100;

export const BUDGET_TARGET_KINDS = ['account', 'tag', 'project', 'jobType', 'person', 'equipment', 'metric'];
export const BUDGET_TARGET_LABELS = { account: 'Account', tag: 'Tag', project: 'Project', jobType: 'Job type', person: 'Person', equipment: 'Equipment', metric: 'Metric' };
export const BUDGET_MEASURES = ['money', 'hours', 'metric'];
export const BUDGET_MEASURE_LABELS = { money: 'Money', hours: 'Hours', metric: 'Metric' };
export const BUDGET_PERIODS = ['month', 'quarter', 'year'];
export const BUDGET_PERIOD_LABELS = { month: 'Month', quarter: 'Quarter', year: 'Year' };
/** Which targets each measure can use (money is ledger legs; hours are sessions; a metric is its values). */
export const BUDGET_TARGETS_FOR = {
  money: ['account', 'tag', 'project', 'jobType', 'person', 'equipment'],
  hours: ['project', 'tag', 'jobType', 'person', 'equipment'],
  metric: ['metric', 'project', 'tag', 'jobType', 'person', 'equipment'],
};
const MONTHS_IN = { month: 1, quarter: 3, year: 12 };

const ymd = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const lastOf = (y, m) => new Date(y, m, 0).getDate();
const addMonths = (y, m, n) => { const t = new Date(y, m - 1 + n, 1); return { y: t.getFullYear(), m: t.getMonth() + 1 }; };
const dayCount = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / DAY) + 1;

/** The fiscal start month (1–12) of a budget row ('MM-DD'), default January. */
export const fiscalStartMonth = (b) => { const m = Number(String(b?.fiscalStart || '01-01').slice(0, 2)); return m >= 1 && m <= 12 ? m : 1; };

/** The period holding `today` (offset = periods before / after) → { from, to, index, months } — as the books. */
export function periodRange(period, fsMonth, today, offset = 0) {
  const [y, m] = today.split('-').map(Number);
  const fs = fsMonth >= 1 && fsMonth <= 12 ? fsMonth : 1;
  const fyY = m >= fs ? y : y - 1;
  const k = (m - fs + 12) % 12;
  const len = MONTHS_IN[period] || 1;
  const index = Math.floor(k / len) + offset;
  const start = addMonths(fyY, fs, index * len);
  const end = addMonths(start.y, start.m, len - 1);
  const months = [];
  for (let i = 0; i < len; i++) months.push(addMonths(start.y, start.m, i).m);
  return { from: ymd(start.y, start.m, 1), to: ymd(end.y, end.m, lastOf(end.y, end.m)), index, months };
}

/** What a period is budgeted: the amount, or its seasonal share of the year by the months' weights (as the books). */
export function periodAmount(b, months) {
  if (b.spread !== 'seasonal' || !Array.isArray(b.weights) || b.weights.length !== 12) return round2(num(b.amount));
  const total = b.weights.reduce((a, w) => a + num(w), 0);
  if (!(total > 0)) return round2(num(b.amount));
  const year = num(b.amount) * (12 / (MONTHS_IN[b.period] || 1));
  return round2((year * months.reduce((a, m) => a + num(b.weights[m - 1]), 0)) / total);
}

const workSession = (s, agents) => s && s.startedAt && !s.overlay && s.type !== 'travel' && !s.deletedAt && !agents.has(s.ownerUid);
const sessionMs = (s, now) => (s.endedAt ? (num(s.duration) || Math.max(0, num(s.endedAt) - num(s.startedAt))) : Math.max(0, now - num(s.startedAt)));

/**
 * Hours toward a budget's target in [from, to] (dates, whole local days). Sessions count by their start day.
 * tag = the project's tags or the session's task's tags (merged tags read as their target).
 */
export function hoursActual(b, { sessions = [], projects = [], tasks = [], jobs = [], departments = [], tagIndex = null, agents = new Set(), me = null, now = Date.now() }, from, to) {
  const kind = b?.target?.kind; const id = b?.target?.id;
  if (!kind || !id || !BUDGET_TARGETS_FOR.hours.includes(kind)) return null;
  const projById = new Map(projects.map((p) => [p.id, p]));
  const taskById = new Map(tasks.map((t) => [t.id, t]));
  const jobById = new Map(jobs.map((j) => [j.id, j]));
  const deptById = new Map(departments.map((d) => [d.id, d]));
  const want = kind === 'tag' ? canonTag(tagIndex ? resolveTags([id], tagIndex)[0] || id : id) : id;
  let ms = 0;
  for (const s of sessions) {
    if (!workSession(s, agents)) continue;
    const d = localDateStr(num(s.startedAt));
    if (d < from || d > to) continue;
    const p = s.projectId ? projById.get(s.projectId) || { id: s.projectId } : null;
    let hit = false;
    if (kind === 'project') hit = s.projectId === id;
    else if (kind === 'person') hit = (s.ownerUid || me) === id;
    else if (kind === 'equipment') hit = !!p && Array.isArray(p.equipment) && p.equipment.includes(id);
    else if (kind === 'jobType') hit = !!p && jobTypeOf(p, p.bookProjectId ? jobById.get(p.bookProjectId) || null : null, deptById.get(p.departmentId) || null) === id;
    else if (kind === 'tag') {
      const t = s.taskId ? taskById.get(s.taskId) : null;
      const tags = [...(p?.tags || []), ...(t?.tags || [])];
      hit = (tagIndex ? resolveTags(tags, tagIndex) : tags).map(canonTag).includes(want);
    }
    if (hit) ms += sessionMs(s, now);
  }
  return round2(ms / H);
}

/** A metric budget's actual in [from, to]: the metric's values (auto or stored) on the target, by its agg. */
export function metricActual(b, { metrics = [], metricValues = [], metricCtx = {} }, from, to) {
  const def = metrics.find((m) => m.id === (b.metricId || (b.target?.kind === 'metric' ? b.target.id : null)));
  if (!def) return null;
  const rows = valuesFor(def, { values: metricValues, ctx: metricCtx });
  const kind = b.target?.kind; const id = b.target?.id;
  const agg = def.agg || 'sum';
  if (kind === 'metric' || kind === 'equipment') {
    const inRange = rows.filter((r) => r.date && r.date >= from && r.date <= to && (kind === 'metric' || (r.target?.kind === 'equipment' && r.target.id === id)));
    return inRange.length ? aggregate(inRange, agg) : 0;
  }
  const by = kind === 'person' ? 'person' : kind;
  const key = kind === 'tag' ? canonTag(id) : id;
  const hit = rollup(rows, { by, from, to, agg, ctx: metricCtx }).find((g) => String(g.key) === String(key));
  return hit ? num(hit.value) : 0;
}

/**
 * The CFO budget rows with their BattleOps actuals filled in: hours (sessions) and metric (values) budgets get
 * actual, pct, burn per day, the straight-line forecast to the period end, the alert reached — and their rollover,
 * worked out here (the books only roll money budgets). Money rows pass through as the books sent them.
 *   rows = opsCfo/now.budgets; ctx = { sessions, projects, tasks, jobs, departments, tagIndex, agents, me, now,
 *          metrics, metricValues, metricCtx }; today = YYYY-MM-DD.
 */
export function budgetActuals(rows = [], ctx = {}, today = localDateStr(ctx.now || Date.now())) {
  return (rows || []).filter((b) => b && b.active !== false).map((b) => {
    if (b.actual != null || b.measure === 'money') return { ...b, basis: 'booked' };
    const actualIn = (from, to) => (b.measure === 'hours' ? hoursActual(b, ctx, from, to) : metricActual(b, ctx, from, to));
    const fsm = fiscalStartMonth(b);
    const r = periodRange(b.period || 'month', fsm, today);
    const from = b.from || r.from; const to = b.to || r.to;
    const base = periodAmount(b, r.months);
    let carry = 0;
    if (b.rollover) {
      for (let i = r.index - 1; i >= 0; i--) {
        const p = periodRange(b.period || 'month', fsm, today, i - r.index);
        const a = actualIn(p.from, p.to);
        carry += periodAmount(b, p.months) - num(a);
      }
    }
    const budget = round2(base + carry);
    const actual = actualIn(from, today < to ? today : to);
    if (actual == null) return { ...b, basis: 'booked' };
    const days = dayCount(from, to);
    const elapsed = Math.min(days, Math.max(1, dayCount(from, today)));
    const pct = budget > 0 ? Math.round((actual / budget) * 1000) / 10 : null;
    const burnPerDay = round2(actual / elapsed);
    const reached = pct == null ? [] : (b.alerts || [80, 100]).filter((a) => pct >= a);
    return {
      ...b, from, to, days, daysElapsed: elapsed, budget, carry: round2(carry), actual: round2(actual), pct, burnPerDay,
      forecastEnd: round2(burnPerDay * days), alert: reached.length ? reached[reached.length - 1] : null, basis: 'booked',
    };
  });
}

/** A budget row's amount per month (its period's amount without rollover ÷ the months in it). */
export function monthlyBudget(b) {
  const len = MONTHS_IN[b?.period] || 1;
  const base = b?.budget != null ? num(b.budget) - num(b.carry) : num(b?.amount);
  return round2(base / len);
}

/**
 * Forward overhead per billable hour (CFO 5.2): the overhead budgeted a month — money budgets on accounts (the
 * picker offers expense accounts) and on tags of kind `internal` — ÷ the billable hours planned a month.
 * → { monthly, plannedHours, perHour, lines: [{ id, name, monthly }] }
 */
export function forwardOverhead(rows = [], { tagIndex = null, plannedHours = null } = {}) {
  const lines = [];
  for (const b of rows || []) {
    if (!b || b.active === false || b.measure !== 'money') continue;
    const k = b.target?.kind;
    const tagKind = k === 'tag' && tagIndex ? tagIndexOf(tagIndex).get(canonTag(b.target.id))?.kind : null;
    if (k !== 'account' && !(k === 'tag' && tagKind === 'internal')) continue;
    lines.push({ id: b.id, name: b.name, monthly: monthlyBudget(b) });
  }
  const monthly = round2(lines.reduce((a, l) => a + l.monthly, 0));
  const h = num(plannedHours);
  return { monthly, plannedHours: h > 0 ? h : null, perHour: h > 0 && lines.length ? round2(monthly / h) : null, lines };
}

/**
 * Budgets that reached an alert level (80 / 100 %) and haven't been shown yet. `seen` = Set of keys already shown;
 * a key is per budget, period and level, so each level shows once a period. → [{ key, row }]
 */
export function budgetAlertsDue(rows = [], seen = new Set()) {
  const out = [];
  for (const b of rows || []) {
    if (!b || b.alert == null || b.active === false) continue;
    const key = `${b.id}|${b.from}|${b.alert}`;
    if (!seen.has(key)) out.push({ key, row: b });
  }
  return out;
}

/** "312 h" / "$1,240" / "41 L" — a budget figure in its measure. */
export function budgetFigure(b, v, metricDef = null) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (b?.measure === 'hours') return `${Math.round(n * 10) / 10} h`;
  if (b?.measure === 'metric') return `${Math.round(n * 100) / 100}${metricDef?.unit ? ` ${metricDef.unit}` : ''}`;
  const a = Math.abs(n);
  return `${n < 0 ? '−' : ''}$${a >= 100 ? Math.round(a).toLocaleString('en-CA') : a.toFixed(2).replace(/\.00$/, '')}`;
}
